import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ShareColumn, WorkType } from '../../shared/types';
import { COLUMN_LABELS } from '../../shared/types';
import { api, ApiError, type PublicPayload } from '../lib/api';
import { formatDuration } from '../lib/duration';
import { formatDayMonth, formatFullDate, formatINR, formatWeekday } from '../lib/format';
import type { ExportRow } from '../lib/export';
import { WorkTypeBadge } from '../components/WorkTypeBadge';
import { ExportDialog } from '../components/ExportDialog';
import { EmptyState, Spinner } from '../components/ui';

const NUMERIC: ShareColumn[] = ['minutes', 'amount'];

function Cell({ column, value }: { column: ShareColumn; value: unknown }) {
  if (column === 'work_date') {
    const iso = String(value);
    return (
      <td className="nums whitespace-nowrap">
        <span className="font-semibold">{formatDayMonth(iso)}</span>
        <span className="ml-1.5 text-[11px] text-[var(--color-ink-muted)]">{formatWeekday(iso)}</span>
      </td>
    );
  }
  if (column === 'work_type') {
    return (
      <td>
        <WorkTypeBadge type={value as WorkType} />
      </td>
    );
  }
  if (column === 'minutes') {
    return <td className="num-cell font-semibold whitespace-nowrap">{formatDuration(Number(value))}</td>;
  }
  if (column === 'amount') {
    return <td className="num-cell whitespace-nowrap">{formatINR(Number(value))}</td>;
  }
  return <td className={column === 'remark' ? 'text-[var(--color-ink-soft)]' : ''}>{String(value ?? '')}</td>;
}

/**
 * The read-only page behind a share link. It renders whatever columns the
 * server sent — it does no filtering of its own, because the payload already
 * excludes anything the owner did not share.
 */
export function PublicView() {
  const { token = '' } = useParams();
  const [data, setData] = useState<PublicPayload | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .publicView(token)
      .then(setData)
      .catch((e) =>
        setError(
          e instanceof ApiError
            ? { status: e.status, message: e.message }
            : { status: 0, message: 'This link could not be loaded.' },
        ),
      );
  }, [token]);

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[var(--color-paper)] px-5">
        <div className="card max-w-md px-6 py-8 text-center">
          <h1 className="text-xl">
            {error.status === 410 ? 'This link is no longer active' : 'Link not found'}
          </h1>
          <p className="mt-2.5 text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
            {error.message} If you need access, ask whoever sent it for a fresh link.
          </p>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-[var(--color-paper)]">
        <Spinner label="Loading timesheet" />
      </main>
    );
  }

  const showAmounts = data.columns.includes('amount');

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <header className="border-b border-[var(--color-line)] bg-white">
        <div className="mx-auto flex max-w-[76rem] flex-wrap items-center justify-between gap-4 px-5 py-5">
          <div>
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-7 w-7 items-center justify-center rounded-[9px] font-display text-[13px] font-bold text-white"
                style={{ background: 'linear-gradient(135deg, var(--color-coral-500), var(--color-blue-600))' }}
                aria-hidden
              >
                T
              </span>
              <h1 className="text-[20px]">{data.label}</h1>
            </div>
            <p className="mt-1.5 text-[13px] text-[var(--color-ink-muted)]">
              {formatFullDate(data.range.from)} – {formatFullDate(data.range.to)} · read-only
            </p>
          </div>
          {data.rows.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setExporting(true)}>
              Export
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-[76rem] px-5 py-7">
        {data.rows.length === 0 ? (
          <div className="card">
            <EmptyState
              title="Nothing logged in this range"
              description="There are no timesheet entries for the period this link covers."
            />
          </div>
        ) : (
          <div className="table-shell hidden md:block">
            <div className="table-scroll">
              <table className="sheet">
                <thead>
                  <tr>
                    {data.columns.map((column) => (
                      <th key={column} className={NUMERIC.includes(column) ? '!text-right' : ''}>
                        {COLUMN_LABELS[column]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, index) => (
                    <tr key={index}>
                      {data.columns.map((column) => (
                        <Cell key={column} column={column} value={row[column]} />
                      ))}
                    </tr>
                  ))}
                </tbody>
                {data.totals && (
                  <tfoot>
                    <tr>
                      {data.columns.map((column, index) => {
                        if (column === 'minutes') {
                          return (
                            <td key={column} className="num-cell whitespace-nowrap">
                              {formatDuration(data.totals!.minutes)}
                            </td>
                          );
                        }
                        if (column === 'amount') {
                          return (
                            <td key={column} className="num-cell whitespace-nowrap">
                              {formatINR(data.totals!.amount_inr ?? 0)}
                            </td>
                          );
                        }
                        // Label the total once, in the first non-numeric column.
                        const isFirstLabel =
                          index === data.columns.findIndex((c) => !NUMERIC.includes(c));
                        return <td key={column}>{isFirstLabel ? 'TOTAL' : ''}</td>;
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {/* Small screens get the same content stacked, with the totals pinned. */}
        {data.rows.length > 0 && (
          <div className="md:hidden">
            <ul className="space-y-2.5">
              {data.rows.map((row, index) => (
                <li
                  key={index}
                  className="rounded-[var(--radius-card)] border border-[var(--color-cyan-200)] bg-[var(--color-cyan-50)] px-4 py-3"
                >
                  {data.columns.map((column) => (
                    <div key={column} className="flex justify-between gap-3 py-0.5 text-[13px]">
                      <span className="text-[var(--color-ink-muted)]">{COLUMN_LABELS[column]}</span>
                      <span className={`text-right ${NUMERIC.includes(column) ? 'nums font-semibold' : ''}`}>
                        {column === 'minutes'
                          ? formatDuration(Number(row[column]))
                          : column === 'amount'
                            ? formatINR(Number(row[column]))
                            : column === 'work_date'
                              ? formatFullDate(String(row[column]))
                              : String(row[column] ?? '—')}
                      </span>
                    </div>
                  ))}
                </li>
              ))}
            </ul>

            {data.totals && (
              <div className="sticky bottom-0 z-20 mt-3 -mx-5 border-t-2 border-[var(--color-gold-400)] bg-[var(--color-gold-100)] px-5 py-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-[15px] font-bold">TOTAL</span>
                  <span className="nums font-display text-[17px] font-bold">
                    {formatDuration(data.totals.minutes)}
                    {showAmounts && (
                      <span className="ml-3 text-[15px]">{formatINR(data.totals.amount_inr ?? 0)}</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {exporting && (
        <ExportDialog
          from={data.range.from}
          to={data.range.to}
          onClose={() => setExporting(false)}
          source={{
            scopeName: data.label,
            showAmounts,
            lockRange: true,
            // The rows are already restricted to the shared columns, so the
            // export can only ever contain a subset of what is on screen.
            load: async () => ({
              rows: data.rows as ExportRow[],
              totals: { minutes: data.totals?.minutes ?? 0, amount: data.totals?.amount_inr },
            }),
          }}
        />
      )}
    </div>
  );
}
